import React, { Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ThemeProvider } from 'styled-components';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
// Router is provided by main.tsx, not needed here
import { theme } from '../../styles/theme';
import { GlobalStyles } from '../../styles/GlobalStyles';
import ProductionErrorBoundary from '../error/ProductionErrorBoundary';
import { TouchOptimizer } from '../mobile/TouchOptimizer';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface OptimizedAppProps {
  children: React.ReactNode;
  enableTouchOptimization?: boolean;
  onAuthError?: (error: any) => void;
}

// Enhanced QueryClient with optimizations
const createOptimizedQueryClient = (onAuthError?: (error: any) => void) => {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        console.error('Query error:', error);
        if (error && typeof error === 'object' && 'type' in error && error.type === 'auth' && onAuthError) {
          onAuthError(error);
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        console.error('Mutation error:', error);
        if (error && typeof error === 'object' && 'type' in error && error.type === 'auth' && onAuthError) {
          onAuthError(error);
        }
      },
    }),
    defaultOptions: {
      queries: {
        // Stale time - how long data is considered fresh
        staleTime: 1000 * 60 * 5, // 5 minutes
        // Cache time - how long unused data stays in cache
        gcTime: 1000 * 60 * 30, // 30 minutes
        // Retry configuration
        retry: (failureCount, error) => {
          // Don't retry on 4xx errors except 408, 429
          if (error instanceof Error && 'status' in error) {
            const status = (error as any).status;
            if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
              return false;
            }
          }
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch configuration
        refetchOnWindowFocus: false,
        refetchOnReconnect: 'always',
        refetchOnMount: true,
      },
      mutations: {
        retry: (failureCount, error) => {
          // Don't retry authentication errors
          if (error && typeof error === 'object' && 'type' in error && error.type === 'auth') {
            return false;
          }
          return failureCount < 1;
        },
        retryDelay: 1000,
      },
    },
  });
};

// Enhanced Error Fallback Component
const ErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
}> = ({ resetErrorBoundary }) => {

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: '#f8f9fa',
      }}
    >
      <div
        style={{
          fontSize: '4rem',
          marginBottom: '1rem',
        }}
      >
        😔
      </div>
      <h1
        style={{
          fontSize: '1.5rem',
          marginBottom: '1rem',
          color: '#343a40',
        }}
      >
        Something went wrong
      </h1>
      <p
        style={{
          fontSize: '1rem',
          color: '#6c757d',
          marginBottom: '2rem',
          maxWidth: '500px',
        }}
      >
        We're sorry, but something unexpected happened. Our team has been notified
        and is working on a fix.
      </p>
      <button
        onClick={resetErrorBoundary}
        style={{
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          padding: '0.75rem 2rem',
          borderRadius: '0.375rem',
          fontSize: '1rem',
          cursor: 'pointer',
          transition: 'background-color 0.2s',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#0056b3';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = '#007bff';
        }}
      >
        Try Again
      </button>
    </div>
  );
};

// Enhanced Loading Component
const AppLoadingFallback: React.FC = () => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
      }}
    >
      <LoadingSpinner />
      <p
        style={{
          marginTop: '1rem',
          color: '#6c757d',
          fontSize: '0.875rem',
        }}
      >
        Loading gallery...
      </p>
    </div>
  );
};

// Performance optimization hook
const usePerformanceOptimizations = () => {
  useEffect(() => {
    // Prefetch critical resources
    const prefetchCriticalResources = () => {
      // Prefetch common images
      const commonImages = ['/icons/gallery-icon.svg', '/images/placeholder.jpg'];
      commonImages.forEach((src) => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = src;
        document.head.appendChild(link);
      });
    };

    // Optimize images with Intersection Observer
    const optimizeImages = () => {
      const images = document.querySelectorAll('img[data-src]');
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            img.src = img.dataset.src!;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        });
      });

      images.forEach((img) => imageObserver.observe(img));
    };

    // Setup performance optimizations
    prefetchCriticalResources();
    optimizeImages();

    // Optimize scroll performance
    let ticking = false;
    const optimizedScrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Handle scroll logic here
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', optimizedScrollHandler, { passive: true });

    return () => {
      window.removeEventListener('scroll', optimizedScrollHandler);
    };
  }, []);
};

// Network status monitoring
const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkSpeed, setNetworkSpeed] = useState<'fast' | 'slow' | 'offline'>('fast');

  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
      setNetworkSpeed(navigator.onLine ? 'fast' : 'offline');
    };

    const updateNetworkSpeed = () => {
      // @ts-ignore - NetworkInformation API
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (connection) {
        const { effectiveType, downlink } = connection;
        if (effectiveType === '4g' && downlink > 2) {
          setNetworkSpeed('fast');
        } else {
          setNetworkSpeed('slow');
        }
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    
    // @ts-ignore
    if (navigator.connection) {
      // @ts-ignore
      navigator.connection.addEventListener('change', updateNetworkSpeed);
    }

    updateNetworkSpeed();

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      // @ts-ignore
      if (navigator.connection) {
        // @ts-ignore
        navigator.connection.removeEventListener('change', updateNetworkSpeed);
      }
    };
  }, []);

  return { isOnline, networkSpeed };
};

export const OptimizedApp: React.FC<OptimizedAppProps> = ({
  children,
  enableTouchOptimization = true,
  onAuthError,
}) => {
  const [queryClient] = useState(() => createOptimizedQueryClient(onAuthError));
  useNetworkStatus();

  // Apply performance optimizations
  usePerformanceOptimizations();

  const AppContent = () => (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(error: Error, errorInfo: React.ErrorInfo) => {
            console.error('App Error:', error, errorInfo);
          }}
        >
          <ProductionErrorBoundary
            enableReporting={false}
            reportingEndpoint="/api/v1/errors"
          >
            {enableTouchOptimization ? (
              <TouchOptimizer
                enableFastClick={true}
                enableScrollSmoothing={false}
                preventZoom={false}
                optimizeRendering={true}
              >
                <Suspense fallback={<AppLoadingFallback />}>
                  {children}
                </Suspense>
              </TouchOptimizer>
            ) : (
              <Suspense fallback={<AppLoadingFallback />}>
                {children}
              </Suspense>
            )}
          </ProductionErrorBoundary>
        </ErrorBoundary>
      </QueryClientProvider>
    </ThemeProvider>
  );

  return <AppContent />;
};

export default OptimizedApp;
